/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import getProductList, { GetProductListInput } from './get-product-list';
import getProductListLines, { GetProductListLinesInput } from './get-product-list-lines';
import { QuickOrderTemplate } from './utilities/quick-order-templates';

/**
 * Input class for the GetProductList data action.
 */
export class GetFullProductListInput implements IActionInput {
    public productListId: string | undefined;

    public constructor(productListId?: string) {
        this.productListId = productListId;
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'GetFullProductList';

    /**
     * Get Cachetype Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'GetFullProductList';

    /**
     * Get Cachetype.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetProductList method.
 * @param inputData - The input data passed to the createInput method.
 * @param productListId - ProductListId.
 * @returns - Input for data action.
 */
export const createGetFullProductListInput = (inputData: ICreateActionContext, productListId?: string): GetFullProductListInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full product list. User is not authenticated.');
    }

    return new GetFullProductListInput(productListId);
};

/**
 * The action method for the GetFullProductList data action.
 * @param input - The action input.
 * @param context - The action context.
 * @returns - Quick Order Template object.
 */
export async function getFullProductListAction(input: GetFullProductListInput, context: IActionContext): Promise<QuickOrderTemplate> {
    if (!context.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full quick order template. User is not authenticated.');
    }

    const inputParameters = new GetProductListInput(input.productListId);
    const quickorderTemplate = await getProductList(inputParameters, context).then(productLists => {
        const inputLines = new GetProductListLinesInput(productLists[0].Id);
        return getProductListLines(inputLines, context).then(linesState => {
            return new QuickOrderTemplate(productLists[0], linesState.lines, linesState.totalCount);
        });
    });
    return quickorderTemplate;
}

/**
 * The GetFullProductList Data Action
 * Returns order templates.
 */
export const getFullProductListActionDataAction = createObservableDataAction<QuickOrderTemplate>({
    id: '@msdyn365-commerce-modules/retail-actions/get-full-product-list',
    action: getFullProductListAction as IAction<QuickOrderTemplate>,
    input: createGetFullProductListInput
});

export default getFullProductListActionDataAction;
